import { sectionRenderer } from '@theme/section-renderer';

/**
 * Adds the free gift to the cart when the cart reaches the goal, and removes it if the cart
 * drops back below it. The bar's state is rendered by Liquid, so this only has to reconcile
 * "should have the gift" (data-qualifies) with "has the gift" (data-gift-qty) after each render.
 */
class FreeGiftBar extends HTMLElement {
  static observedAttributes = ['data-qualifies', 'data-gift-qty'];

  #busy = false;

  connectedCallback() {
    this.#reconcile();
  }

  attributeChangedCallback() {
    // The cart re-render can update this element in place instead of replacing it.
    this.#reconcile();
  }

  async #reconcile() {
    if (this.#busy) return;

    const variantId = Number(this.dataset.variantId);
    const qualifies = this.dataset.qualifies === 'true';
    const giftQty = Number(this.dataset.giftQty) || 0;
    if (!variantId) return;

    let request;
    if (qualifies && giftQty === 0) {
      request = fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: { _free_gift: 'true' } }] }),
      });
    } else if (!qualifies && giftQty > 0) {
      request = fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: String(variantId), quantity: 0 }),
      });
    } else if (qualifies && giftQty > 1) {
      request = fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: String(variantId), quantity: 1 }),
      });
    } else {
      return;
    }

    this.#busy = true;
    try {
      const response = await request;
      if (!response.ok) throw new Error('Free gift could not be updated');

      const sectionId = this.closest('cart-items-component')?.dataset.sectionId;
      if (sectionId) await sectionRenderer.renderSection(sectionId, { cache: false });
    } catch (error) {
      console.error(error);
    } finally {
      this.#busy = false;
    }
  }
}

if (!customElements.get('free-gift-bar')) {
  customElements.define('free-gift-bar', FreeGiftBar);
}
